// 🔧 leaderboard_print.js
// ✅ Purpose: Admin-only print-friendly leaderboard view
// ✅ Features:
// - Shows top 3 entries per category by vote count
// - Includes Best in Show winners if enabled
// - Loads Firestore votes and cars
// - Auto-opens print dialog on load
// - Adds event name at top, vote totals, timestamp footer
// - Inserts page break every 2 categories
// - Auto-closes window after printing

import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useCategories } from "../../hooks/use_categories";
import { useEventContext } from "../../hooks/use_event_context";
import { useVotingStatus } from "../../hooks/use_voting_status";
import {
  getEventVotingType,
} from "../../utils/event_voting_type_helpers";
import {
  calculateRankedChoiceResults,
} from "../../utils/ranked_choice_helpers";
import {
  getVotingTypeLabel,
} from "../../utils/voting_engine_registry";
import {
  isVoteCountStrategy,
  isRankedEliminationStrategy,
  isAverageScoreStrategy,
} from "../../utils/result_strategy_helpers";
import {
  buildJudgedPointsResults,
} from "../../utils/points_results_helpers";

const LeaderboardPrint = () => {

  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });
  const { categories } = useCategories(eventId);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rankedLeaderboard, setRankedLeaderboard] = useState([]);
  const [pointsResults, setPointsResults] = useState(null);
  const [bestInShow, setBestInShow] = useState([]);
  const [eventLogo, setEventLogo] = useState(null);
  const [eventName, setEventName] = useState("");
  const { bestInShowEnabled } = useVotingStatus(eventId);
  const votingType = getEventVotingType(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);
  const isVoteCountLeaderboard =
    isVoteCountStrategy(votingType);
  const isRankedLeaderboard =
    isRankedEliminationStrategy(votingType);
  const isPointsLeaderboard =
    isAverageScoreStrategy(votingType);

  useEffect(() => {
    const fetchData = async () => {
      const votesSnap = await getDocs(collection(db, "events", eventId, "votes"));
      const allVotes = votesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // =====================================================
      // 🚗 Load all entries once
      // =====================================================

      const carsSnap = await getDocs(
        collection(db, "events", eventId, "cars")
      );

      const carMap = {};

      carsSnap.docs.forEach((docSnap) => {
        const car = {
          id: docSnap.id,
          ...docSnap.data(),
        };

        carMap[docSnap.id] = car;

        if (car.cid) {
          carMap[car.cid] = car;
        }
      });

      if (isRankedLeaderboard) {

        const rankedGroups = categories
          .filter(cat => String(cat.id) !== "0")
          .sort((a, b) =>
            String(a?.name ?? "").localeCompare(
              String(b?.name ?? "")
            )
          )
          .map(cat => {

            const categoryVotes = allVotes.filter(
              vote => String(vote.catid) === String(cat.id)
            );

            const candidateIds = [
              ...new Set(
                categoryVotes.flatMap(vote =>
                  Array.isArray(vote.rankings)
                    ? vote.rankings.map(r => r.cid)
                    : []
                )
              ),
            ];

            const results = calculateRankedChoiceResults({
              votes: categoryVotes,
              candidateIds,
            });

            const placements = (results.leaderboardPlacements || [])
              .slice(0, 5)
              .map((placement) => {

                const car = carMap[placement.cid] || {};

                return {
                  ...placement,
                  regcnt: car.regcnt || "",
                  description: car.description || "",
                  name: `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim(),
                };
              });

            return {
              category: cat.name,
              placements,
              totalBallots: results.totalBallots || 0,
            };
          });

        setRankedLeaderboard(rankedGroups);
        setEventName(eventDoc?.name || "Event");
        return;
      }

      if (isPointsLeaderboard) {

        const results = buildJudgedPointsResults({
          votes: allVotes,
          carMap,
          limit: 3,
        });

        setPointsResults(results);
        setEventName(eventDoc?.name || "Event");
        return;
      }

      // 🗳 STANDARD VOTING ONLY
      // Assumes one vote document equals one vote.
      // Ranked Choice and Points voting will require
      // different aggregation logic.
      const categoryMap = {};
      for (const vote of allVotes) {
        const key = `${vote.catid}_${vote.cid}`;
        if (!categoryMap[vote.catid]) categoryMap[vote.catid] = {};
        categoryMap[vote.catid][key] = categoryMap[vote.catid][key] || { ...vote, count: 0 };
        categoryMap[vote.catid][key].count++;
      }

      const topCats = categories
        .filter(cat => String(cat.id) !== "0")
        .sort((a, b) =>
          String(a?.name ?? "").localeCompare(
            String(b?.name ?? "")
          )
        )
        .map(cat => {
          const entries = Object.values(categoryMap[cat.id] || {});
          const top = entries
            .sort((a, b) => b.count - a.count)
            .slice(0, 3)
            .map(v => {
              const car = carMap[v.cid] || {};
              return {
                ...v,
                regcnt: car.regcnt,
                description: car.description,
                name: `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim(),
              };
            });
          return { category: cat.name, cars: top, totalVotes: entries.reduce((sum, v) => sum + v.count, 0) };
        });

      const bisVotes = allVotes.filter(v => v.bestInShow);
      const bisMap = {};
      for (const vote of bisVotes) {
        if (!bisMap[vote.cid]) bisMap[vote.cid] = { ...vote, count: 0 };
        bisMap[vote.cid].count++;
      }

      const bisList = Object.values(bisMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map(v => {
          const car = carMap[v.cid] || {};
          return {
            ...v,
            regcnt: car.regcnt,
            description: car.description,
            name: `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim(),
          };
        });

      setEventName(eventDoc?.name || "Event");

      setLeaderboard(topCats);
      setBestInShow(bisList);
    };

    if (eventId && categories.length > 0) fetchData();
  }, [
    eventId,
    categories,
    isRankedLeaderboard,
    isPointsLeaderboard,
    eventDoc,
  ]);

  useEffect(() => {
    const fetchEventSettings = async () => {
        if (!eventId) return;
        const settingsRef = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(settingsRef);
        if (snap.exists()) {
        const settings = snap.data();
        if (settings.eventName) {
          setEventName(settings.eventName);
        }
        if (settings.eventLogo) setEventLogo(settings.eventLogo);
        }
    };
    fetchEventSettings();
    }, [eventId]);

  // 🎯 Inject style, print, and close
    useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.setAttribute("id", "print-orientation-style");
    styleTag.innerHTML = `
        @page {
        size: A4 portrait;
        margin: 1in;
        }
    `;
    document.head.appendChild(styleTag);
    document.body.classList.add("leaderboard-print-portrait");

    // 🖨️ Open print dialog
    setTimeout(() => {
        window.print();

        // 🧠 Prompt after print completes
        setTimeout(() => {
        const confirmClose = window.confirm("✅ Printing complete. Would you like to close this page?");
        if (confirmClose) {
            window.close();
        }
        // 🧹 Clean up whether or not they close
        document.body.classList.remove("leaderboard-print-portrait");
        document.getElementById("print-orientation-style")?.remove();
        }, 2000);
    }, 4000);
    }, []);

    if (eventLoading || !ready) {
      return null;
    }

    if (
      !isVoteCountLeaderboard &&
      !isRankedLeaderboard &&
      !isPointsLeaderboard
    ) {
      return (
        <div className="container py-5">
          <div className="alert alert-warning">
            Printable leaderboard is not implemented for{" "}
            <strong>{votingType}</strong> voting.
          </div>
        </div>
      );
    }

  return (
    <div className="leaderboard-print-card">
      <div className="d-flex align-items-center mb-3 gap-3">
          {eventLogo && (
              <img
              src={eventLogo}
              alt="Event Logo"
              style={{ height: "60px", objectFit: "contain" }}
              />
          )}
          <h1 className="m-0">{eventName}</h1>
      </div>

      {eventName && (
        <>
          <h4 className="text-muted mb-4">
            🏆 Top Winners
          </h4>

          <p className="text-muted">
            Voting Type: {votingTypeLabel}
          </p>
        </>
      )}
      
      <p className="text-muted mb-4">
        🕒 Generated: {new Date().toLocaleString()}
      </p>

      {bestInShowEnabled && bestInShow.length > 0 && (
        <>
          <h3 className="mt-4 text-warning"><b>Best in Show</b></h3>
          <ol>
            {bestInShow.map((car, idx) => (
              <li key={idx}>
                #{car.regcnt} — {car.description} by {car.name} ({car.count} votes)
              </li>
            ))}
          </ol>
        </>
      )}

      {isRankedLeaderboard && rankedLeaderboard.map((group, idx) => (
        <div
          key={idx}
          className="mt-4 leaderboard-print-section"
        >
          <h4><b>{group.category}</b></h4>
          <p className="text-muted">Total Ballots: {group.totalBallots}</p>

          {group.placements.length > 0 ? (
            <ol>
              {group.placements.map((car) => (
                <li key={`${car.place}-${car.cid}`}>
                  #{car.regcnt} — {car.description} by {car.name}
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-muted">No ranked-choice ballots submitted.</p>
          )}
        </div>
      ))}

      {isPointsLeaderboard && pointsResults && (
        <>
          <div className="mt-4 leaderboard-print-section">

            <h3>
              <b>🏆 Overall Winners</b>
            </h3>

            <ol>
              {pointsResults.overallTopEntries.map((entry) => (
                <li key={entry.cid}>
                  #{entry.regcnt} — {entry.description} by {entry.name}
                  {" "}
                  ({entry.averageScoreDisplay}/{entry.maxBallotPoints})
                </li>
              ))}
            </ol>

          </div>

          {pointsResults.factorLeaderboards.map((factor) => (

            <div
              key={factor.factorId}
              className="mt-4 leaderboard-print-section"
            >

              <h4>
                <b>{factor.factorName}</b>
              </h4>

              <ol>
                {factor.topEntries.map((entry) => (
                  <li key={`${factor.factorId}-${entry.cid}`}>
                    #{entry.regcnt} — {entry.description} by {entry.name}
                    {" "}
                    ({entry.averageScoreDisplay}/{entry.maxPoints})
                  </li>
                ))}
              </ol>

            </div>

          ))}

        </>
      )}

      {isVoteCountLeaderboard && leaderboard.map((group, idx) => (
        <div
          key={idx}
          className="mt-4 leaderboard-print-section"
        >
          <h4><b>{group.category}</b></h4>
          <p className="text-muted">Total Votes: {group.totalVotes}</p>
          <ol>
            {group.cars.map((car, i) => (
              <li key={i}>
                #{car.regcnt} — {car.description} by {car.name} ({car.count} votes)
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
};

export default LeaderboardPrint;
